import axios from "axios";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import {
  attemptShowErrorsInSnackbar,
  useSnackbarActionContext,
} from "../../../context/SnackbarContext";
import { PageHeader } from "@velocity-ehs/component-library";
import { SnackbarType } from "../../sharedComponents/Snackbar";
import {
  addLocationToOrg,
  getOrganizationalUnitDetails,
  removeLocationFromOrg,
} from "../../supportFunctions/graphql/HierarchiesQueries";
import EditLinkedLocations from "./EditLinkedLocations";
import { OrgUnit } from "./Hierarchies";
import LinkLocationsSlider from "./LinkLocationsSlider";
import OrgUnitProfile from "./OrgUnitProfile";
import { useTranslation } from "react-i18next";
import VerticalTabs from "../../sharedComponents/VerticalTabs";
import { useFlags } from "launchdarkly-react-client-sdk";
import GatewayService from "../../../services/GatewayService";

const EditOrgUnit = (): React.ReactElement => {
  const { productChangeTracking } = useFlags();
  const { id } = useParams() as { id: string };
  const [orgUnit, setOrgUnit] = useState<OrgUnit>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showLinkLocations, setShowLinkLocations] = useState<boolean>(false);
  const { showSnackbar } = useSnackbarActionContext();
  const { t } = useTranslation();

  const getOrgUnit = async () => {
    setIsLoading(true);

    try {
      const response = await GatewayService.api?.post(
        "",
        getOrganizationalUnitDetails(id)
      );

      setOrgUnit(response?.data.data.organizationalUnit);
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
    }
  };

  const linkLocations = async (locationIds: string[]) => {
    if (!orgUnit) return;
    setIsLoading(true);
    try {
      await axios.all(
        locationIds.map((id) => {
          return GatewayService.api?.post("", addLocationToOrg(orgUnit.id, id));
        })
      );
      showSnackbar &&
        showSnackbar(SnackbarType.Success, t("location_added_successfully"));
      await getOrgUnit();
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
      setShowLinkLocations(false);
    }
  };

  const onUnlinkLocation = async (locationId: string) => {
    if (!orgUnit) return;
    setIsLoading(true);
    try {
      await GatewayService.api.post(
        "",
        removeLocationFromOrg(orgUnit.id, locationId)
      );
      showSnackbar &&
        showSnackbar(SnackbarType.Success, t("location_removed_successfully"));
      await getOrgUnit();
    } catch (error) {
      attemptShowErrorsInSnackbar(showSnackbar, error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    getOrgUnit();
    // eslint-disable-next-line
  }, []);

  const handleUpdate = (updatedOrgUnit: OrgUnit) => {
    setOrgUnit({ ...orgUnit, ...updatedOrgUnit });
  };

  return (
    <>
      <PageHeader
        title={orgUnit?.name}
        navigationTree={[
          { text: `Accelerate ${t("universal_Home")}`, href: "/" },
          { text: t("universal_Hierarchies"), href: "/hierarchies" },
          { text: t("action_edit"), href: "" },
        ]}
      />
      <VerticalTabs
        includeChangeTracking={productChangeTracking}
        targetIdentifierType="OrganizationalUnit"
        tabs={[
          {
            title: t("universal_Profile"),
            content: (
              <OrgUnitProfile
                onUpdate={handleUpdate}
                orgUnit={orgUnit}
                isLoading={isLoading}
              />
            ),
          },
          {
            title: t("universal_Locations"),
            path: "/locations",
            content: (
              <EditLinkedLocations
                onLinkLocations={() => setShowLinkLocations(true)}
                onUnlinkLocation={onUnlinkLocation}
                orgUnit={orgUnit}
                isLoading={isLoading}
              />
            ),
          },
        ]}
      />
      <LinkLocationsSlider
        closeSliderCallback={() => setShowLinkLocations(false)}
        orgUnitId={orgUnit?.id}
        linkLocations={linkLocations}
        showSlider={showLinkLocations}
      />
    </>
  );
};

export default EditOrgUnit;
